// pages/vendors/soft-skill/index.tsx
import {
  createSoftSkillVendorColumns,
  SoftSkillVendorActionType,
} from "@/colDef/softskilColDef";
import {
  Button,
  Card,
  CardContent,
  CustomAlertDialog,
  DataTable,
  ErrorMessage,
  PageContainer,
  Pagination,
  Spinner,
} from "@/components";
import Search from "@/components/Search";
import SoftSkillVendorDialog from "@/components/formDialog/SoftSkillVendorDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useDeleteSoftSkillVendorMutation,
  useSoftSkillVendorsQuery,
} from "@/hooks/api/softSkillVendor.queries";
import useDebounce from "@/hooks/useDebounce";
import { useAlertDialog, useFormDialog } from "@/hooks/useDialogState";
import { usePagination } from "@/hooks/usePagination";
import {
  SoftSkillVendorParamsType,
  SoftSkillVendorStatus,
  SoftSkillVendorType,
} from "@/services/softSkillVendor.service";
import { Filter, Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const SoftSkillVendorsPage = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    SoftSkillVendorStatus | "all"
  >("all");

  const { page, limit, setPage, resetPage } = usePagination({
    initialLimit: 10,
  });
  const debouncedSearch = useDebounce(searchTerm, 400);

  const deleteDialog = useAlertDialog();
  const vendorDialog = useFormDialog({
    mode: "create" as "create" | "edit",
    vendorId: "",
    vendorData: null as SoftSkillVendorType | null,
  });

  const filters: SoftSkillVendorParamsType = useMemo(
    () => ({
      limit,
      page,
      sortBy: "createdAt",
      sortOrder: "desc",
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(statusFilter !== "all" && { status: statusFilter }),
    }),
    [debouncedSearch, statusFilter, page, limit]
  );

  const { data, isLoading, isError, refetch } =
    useSoftSkillVendorsQuery(filters);
  const deleteMutation = useDeleteSoftSkillVendorMutation(filters);

  const vendors = data?.vendors ?? [];
  const pagination = data?.metaData?.pagination;
  const totalEntries = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  const handleDelete = useCallback(() => {
    deleteMutation.mutate(deleteDialog.state.mantraId, {
      onSuccess: () => deleteDialog.close(),
    });
  }, [deleteMutation, deleteDialog]);

  const handleActionClick = useCallback(
    (payload: SoftSkillVendorActionType) => {
      if (payload.action === "delete") {
        deleteDialog.open({
          message: (
            <span>
              Are you sure you want to delete{" "}
              <strong className="text-gray-800">{payload.name}</strong>? This
              action cannot be undone.
            </span>
          ),
          mantraId: payload.id,
          mantraTitle: payload.name,
          action: "delete",
        });
      } else if (payload.action === "edit") {
        const vendorData = vendors.find((v) => v._id === payload.id) ?? null;
        vendorDialog.open({ mode: "edit", vendorId: payload.id, vendorData });
      }
    },
    [deleteDialog, vendorDialog, vendors]
  );

  const handleCreateClick = () => {
    vendorDialog.open({ mode: "create", vendorId: "", vendorData: null });
  };

  const columns = useMemo(
    () => createSoftSkillVendorColumns(handleActionClick),
    [handleActionClick]
  );

  if (isError) {
    return (
      <ErrorMessage
        message="Failed to load soft skill vendors."
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <PageContainer className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Soft Skill Vendors</h1>
        <Button
          onClick={handleCreateClick}
          className="flex items-center gap-2 bg-gray-900 text-white hover:bg-gray-800"
        >
          <Plus size={16} />
          Add New
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <Search
          placeholder="Search..."
          value={searchTerm}
          onValueChange={(val) => {
            setSearchTerm(val);
            resetPage();
          }}
          className={{ inputGroupClass: "max-w-xs" }}
        />
        <Select
          value={statusFilter}
          onValueChange={(val) => {
            setStatusFilter(val as typeof statusFilter);
            resetPage();
          }}
        >
          <SelectTrigger className="w-36 gap-2">
            <Filter size={14} className="text-gray-500" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Spinner />
            </div>
          ) : (
            <DataTable<SoftSkillVendorType, any>
              columns={columns}
              data={vendors}
              onRowClick={(vendor) =>
                navigate(`/vendors/soft-skill/${vendor._id}`)
              }
            />
          )}
        </CardContent>
      </Card>

      <Pagination
        page={page}
        totalPages={totalPages}
        totalEntries={totalEntries}
        currentCount={vendors.length}
        onPageChange={setPage}
      />

      <SoftSkillVendorDialog
        mode={vendorDialog.state.mode}
        isOpen={vendorDialog.state.isOpen}
        onOpenChange={(open) => !open && vendorDialog.close()}
        filters={filters}
        vendorId={vendorDialog.state.vendorId}
        vendorData={vendorDialog.state.vendorData}
        onCancel={vendorDialog.close}
      />

      <CustomAlertDialog
        isOpen={deleteDialog.state.isOpen}
        onOpenChange={(open) => !open && deleteDialog.close()}
        title="Confirm Deletion"
        description={deleteDialog.state.message}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={deleteDialog.close}
      />
    </PageContainer>
  );
};

export default SoftSkillVendorsPage;
